import type { EmailSignatureV2 } from '@components/EmailSignaturev2/types';
import {
  contactIconsColorHaveChanged,
  getContactDetailsFieldsDesign,
  socialLinksAppearanceHaveChanged,
} from '@modules/email-signatures-helpers';
import { isEqual } from 'es-toolkit';
// es-toolkit/compat: lodash omit is null-safe (omit(undefined) === {})
import { omit } from 'es-toolkit/compat';
import { useMemo } from 'react';

function useContactIconsGeneratorSwitch(
  emailSignatureHasBeenLoaded: boolean,
  initialEmailSignatureHasBeenLoaded: boolean,
  initialEmailSignature: EmailSignatureV2,
  emailSignature: EmailSignatureV2,
): boolean {
  const turnOnContactDetailsIconsGenerator = useMemo(
    () =>
      (emailSignatureHasBeenLoaded &&
        initialEmailSignatureHasBeenLoaded &&
        !isEqual(initialEmailSignature.contactDetails, emailSignature.contactDetails)) ||
      contactIconsColorHaveChanged(initialEmailSignature.design, emailSignature.design),
    [
      emailSignatureHasBeenLoaded,
      initialEmailSignatureHasBeenLoaded,
      emailSignature.contactDetails,
      emailSignature.design,
      initialEmailSignature.contactDetails,
      initialEmailSignature.design,
    ],
  );

  return turnOnContactDetailsIconsGenerator;
}

function useSocialIconsGeneratorSwitch(
  emailSignatureHasBeenLoaded: boolean,
  initialEmailSignatureHasBeenLoaded: boolean,
  initialEmailSignature: EmailSignatureV2,
  emailSignature: EmailSignatureV2,
): boolean {
  const turnOnSocialLinksIconsGenerator = useMemo(
    () =>
      (emailSignatureHasBeenLoaded &&
        initialEmailSignatureHasBeenLoaded &&
        (!isEqual(initialEmailSignature.socialLinks, emailSignature.socialLinks) ||
          !isEqual(
            getContactDetailsFieldsDesign(initialEmailSignature.design),
            getContactDetailsFieldsDesign(emailSignature.design),
          ))) ||
      socialLinksAppearanceHaveChanged(initialEmailSignature.design, emailSignature.design),
    [
      emailSignatureHasBeenLoaded,
      initialEmailSignatureHasBeenLoaded,
      emailSignature.design,
      emailSignature.socialLinks,
      initialEmailSignature.design,
      initialEmailSignature.socialLinks,
    ],
  );

  return turnOnSocialLinksIconsGenerator;
}

function useEnvironmentalIconGeneratorSwitch(
  emailSignatureHasBeenLoaded: boolean,
  initialEmailSignatureHasBeenLoaded: boolean,
  initialEmailSignature: EmailSignatureV2,
  emailSignature: EmailSignatureV2,
): boolean {
  const turnOnEnvironmentalFooterIconGenerator = useMemo(
    () =>
      emailSignatureHasBeenLoaded &&
      initialEmailSignatureHasBeenLoaded &&
      !isEqual(
        omit(initialEmailSignature.environmentalFooter, 'value'),
        omit(emailSignature.environmentalFooter, 'value'),
      ),
    [
      initialEmailSignatureHasBeenLoaded,
      emailSignatureHasBeenLoaded,
      emailSignature.environmentalFooter,
      initialEmailSignature.environmentalFooter,
    ],
  );

  return turnOnEnvironmentalFooterIconGenerator;
}

export {
  useContactIconsGeneratorSwitch,
  useEnvironmentalIconGeneratorSwitch,
  useSocialIconsGeneratorSwitch,
};
